import { domainToASCII } from 'url'

export const capitalize = (str: string): string => {
  return str && str.charAt(0).toUpperCase() + str.slice(1)
}

export const capitalizeEachWord = (str: string): string => {
  return str
    .split(' ')
    .map(word => capitalize(word))
    .join(' ')
}

/**
 * Normalize a string by trimming and converting to lowercase
 */
export const normalizeString = (value: string): string => value.trim().toLowerCase()

/**
 * Convert string to slug-safe string.
 * @param {string} args
 * @returns {string}
 */
export const slugify = (...args: string[]): string => {
  const value = args.join(' ')

  return (
    value
      ?.normalize('NFD') // split an accented letter in the base letter and the accent
      .replaceAll(/[\u0300-\u036F]/g, '') // remove all previously split accents
      .toLowerCase()
      .trim()
      .replaceAll(/[^a-z0-9 ]/g, '') // remove all chars not letters, numbers and spaces (to be replaced)
      .replaceAll(/\s+/g, '-') ?? '' // separator
  )
}

export function isEnumMember<E extends string>(
  value: unknown,
  enumObject: Record<string, E>,
): value is E {
  return Object.values(enumObject).includes(value as E)
}

/**
 * Generate a random key for React elements.
 * Uses Math.random() to create a simple alphanumeric string.
 * @returns {string} Random key string
 */
export const generateRandomKey = (): string => {
  return Math.random().toString(36).slice(2, 11)
}

// Copied from Coda's js-core/utils/string.ts
const PluralAddEsWords = new Set(['canvas'])
const SingularSameAsPluralWords = new Set(['analytics'])

/**
 * Pluralize a phrase based on count.
 * Handles special cases like "canvas" -> "canvases" and "analytics" (same singular/plural).
 * Copied from Coda's implementation.
 */
export function pluralize(phrase: string, count: number = 2): string {
  const words = phrase.split(' ')
  const finalWord = words.at(-1)
  if (!finalWord) {
    return phrase
  }
  const finalWordLower = finalWord.toLowerCase()
  if (Math.abs(count) !== 1 && PluralAddEsWords.has(finalWordLower)) {
    return `${phrase}es`
  }
  return `${phrase}${Math.abs(count) === 1 || SingularSameAsPluralWords.has(finalWordLower) ? '' : 's'}`
}

/**
 * Get displayable version of a network domain, showing both Unicode and ASCII versions if different.
 * For example: "münchen.de" becomes "münchen.de (xn--mnchen-3ya.de)"
 *
 * Based on Coda's browser-shared/packs/helpers.tsx implementation,
 * but using Node.js url module's domainToASCII instead of deprecated punycode.
 *
 * Note: In Node.js, URL constructor converts Unicode to punycode automatically,
 * but we use domainToASCII for explicit conversion to match Coda's behavior exactly.
 */
export function getDisplayableNetworkDomain(networkDomain: string): string {
  try {
    // Use Node.js url module's domainToASCII for explicit punycode conversion
    // This is the modern replacement for punycode.toASCII()
    const asciiDomain = domainToASCII(networkDomain)

    if (asciiDomain === networkDomain) {
      return networkDomain
    }

    return `${networkDomain} (${asciiDomain})`
  } catch {
    // If conversion fails, return original domain
    return networkDomain
  }
}

// ============================================================================
// Agent Meta Tag Utilities
// Ported from: coda/modules/server/frontend/private/helpers/meta_tags_generator.ts
// ============================================================================

const MAX_DESCRIPTION_LENGTH = 160
const MAX_TITLE_LENGTH = 60

/**
 * Replace URLs in text with a replacement string.
 * Used to sanitize meta descriptions by replacing URLs with 'https://...'
 *
 * @param input - Text that may contain URLs
 * @param replacement - String to replace URLs with (default: empty string)
 * @returns Text with URLs replaced
 *
 * Source: coda/modules/server/frontend/private/helpers/meta_tags_generator.ts (_replaceUrls)
 */
function replaceUrls(input: string, replacement: string = ''): string {
  return input.replaceAll(/https?:\/\/[^\s]+(\s)?/g, replacement + '$1')
}

/**
 * Normalize agent title for meta tags.
 * - Capitalizes the agent name
 * - Truncates if too long (respecting word boundaries)
 * - Adds ellipsis if truncated
 * - Always adds "Agent | Superhuman" suffix
 *
 * @param agentName - The agent/pack name
 * @returns Normalized title string for <title> tag
 *
 * Source: coda/modules/server/frontend/private/helpers/meta_tags_generator.ts (_normalizeAgentTitle)
 */
export function normalizeAgentTitle(agentName: string): string {
  const suffix = 'Agent | Superhuman'
  if (!agentName) {
    return suffix
  }

  let title = capitalize(agentName)
  const maxSubtitleLength = MAX_TITLE_LENGTH - (suffix.length + 1)

  if (title.length > maxSubtitleLength) {
    let truncatedTitle = title.slice(0, Math.max(0, maxSubtitleLength))
    if (title.charAt(maxSubtitleLength) !== ' ') {
      const lastSpace = truncatedTitle.lastIndexOf(' ')
      if (lastSpace !== -1) {
        truncatedTitle = truncatedTitle.slice(0, Math.max(0, lastSpace))
      }
    }
    title = truncatedTitle + '...'
  }

  return `${title} ${suffix}`
}

/**
 * Normalize agent description for meta tags.
 * - Uses agent's short description if available, otherwise generates fallback
 * - Replaces URLs with 'https://...'
 * - Truncates to max description length
 *
 * @param agentName - The agent/pack name
 * @param agentShortDescription - Optional short description from agent data
 * @returns Normalized description string for meta description tag
 *
 * Source: coda/modules/server/frontend/private/helpers/meta_tags_generator.ts (_normalizeAgentDescription)
 */
export function normalizeAgentDescription(
  agentName: string,
  agentShortDescription?: string,
): string {
  let description =
    agentShortDescription ||
    `Discover, customize, and deploy the ${agentName} agent in the Superhuman AI Store.`

  description = replaceUrls(description, 'https://...')
  description = description.slice(0, Math.max(0, MAX_DESCRIPTION_LENGTH))

  return description
}
